import i18n from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useFirstWin } from "@/lib/onboarding";
import { RefreshButton } from "@/components/shared/refreshButton/RefreshButton";
import { validationStatus } from "./validationStatus";
import {
	IconAlertTriangle,
	IconCheck,
	IconExternalLink,
	IconShieldCheck,
} from "@tabler/icons-react";
import { cn } from "@/lib/cn";

interface ValidateRailPanelProps {
	/** Document validation snapshot — typically from useEditorStore. */
	documentValidation?: {
		valid: boolean;
		error?: string;
		warnings?: string[];
	} | null;
	/**
	 * Accepted and ignored.
	 *
	 * This used to decide the panel's verdict — a saved document was reported
	 * as a valid one. Saving says nothing about whether the document compiles,
	 * so it no longer has a vote. Kept on the interface because callers still
	 * pass it and removing it is a separate change.
	 */
	saveStatus?: "idle" | "saving" | "saved" | "error" | "offline";
	/** Block count for the header summary line. */
	blockCount?: number;
	/** Run a document-level validation pass inline (no full-page trip). */
	onValidate?: () => void | Promise<void>;
	/** True while an inline validation pass is running. */
	isValidating?: boolean;
	/** Click handler for the "Open in studio" link. */
	onOpenStudio?: () => void;
	/** True when the document changed after the result above was produced. */
	stale?: boolean;
}

/**
 * Right-rail Validate panel — per user direction (2026-05-11) the
 * validation surface lives as a 4th tab next to Outline / Comments /
 * History. Inline doc-level snapshot now; per-block validation calls
 * land in a follow-up.
 *
 * Header chip mirrors the TopBar status pip (same color/state) so the
 * two surfaces feel like the same signal.
 *
 * Click "Open in studio" for the full validation surface (per-block
 * errors, render log, fix suggestions) until those land inline here.
 */
export function ValidateRailPanel({
	documentValidation,
	blockCount,
	onValidate,
	isValidating = false,
	onOpenStudio,
	stale = false,
}: ValidateRailPanelProps) {
	// Only a real, current pass earns "ok" — see validationStatus for what this
	// used to claim and why it was wrong.
	const status = useMemo(
		() => validationStatus(documentValidation, stale),
		[documentValidation, stale],
	);

	// First win 1a (Olivia, 21 Oct): the account's first clean pass gets one line, until the
	// panel closes or Validate runs again.
	const firstWin = useFirstWin("first-validate-pass");
	const [firstPass, setFirstPass] = useState(false);
	useEffect(() => {
		setFirstPass(false);
		if (status !== "ok") return;
		let live = true;
		void firstWin().then((first) => { if (live && first) setFirstPass(true); });
		return () => { live = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps -- once per validation result
	}, [status, documentValidation]);

	const headerLabel = (() => {
		if (status === "error") return i18n.t("editor:validate.issuesToFix");
		if (status === "warning")
			return i18n.t("editor:validate.warnings", { count: documentValidation?.warnings?.length ?? 0 });
		if (status === "stale") return i18n.t("editor:validate.changedSinceValidation");
		if (status === "ok") return blockCount ? i18n.t("editor:validate.validBlocks", { count: blockCount }) : i18n.t("editor:validate.valid");
		return i18n.t("editor:validate.notValidatedYet");
	})();

	const headerIcon = (() => {
		if (status === "error") return <IconAlertTriangle size={14} />;
		if (status === "warning") return <IconAlertTriangle size={14} />;
		if (status === "ok") return <IconCheck size={14} />;
		return <IconShieldCheck size={14} />;
	})();

	return (
		<div
			data-testid="validate-rail-panel"
			data-status={status}
			className="flex flex-col h-full"
		>
			{/* Status chip — mirrors TopBar pip. */}
			<div
				className={cn(
					"flex items-center gap-2 px-4 py-3 text-[12px] font-medium border-b border-[var(--lilia-border)]",
					status === "error" && "text-[var(--lilia-danger,#dc2626)]",
					status === "warning" && "text-[var(--lilia-warning,#d97706)]",
					status === "ok" && "text-[var(--lilia-success,#16a34a)]",
					status === "idle" && "text-[var(--lilia-text-muted)]",
				)}
			>
				{headerIcon}
				<span className="flex-1">{headerLabel}</span>
			</div>

			<div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
				{/* Run validation inline — no need to open the full page (founder
				    request). Per-block detail still lives behind "Open full
				    validation" below. */}
				{onValidate && (
					// The one RefreshButton (saved-moment handoff §5): working while
					// the pass runs, a green ✓ when it lands.
					<RefreshButton
						testId="validate-run"
						label={documentValidation ? i18n.t("editor:validate.reRunValidation") : i18n.t("editor:validate.validateNow")}
						text={documentValidation ? i18n.t("editor:validate.reRunValidation") : i18n.t("editor:validate.validateNow")}
						onRefresh={onValidate}
						busy={isValidating}
						iconSize={14}
					/>
				)}

				{documentValidation?.error && (
					<div data-testid="validate-error" className="rounded-md border border-[var(--lilia-danger,#dc2626)]/30 bg-[var(--lilia-danger,#dc2626)]/5 p-3 text-[12px] text-[var(--lilia-text)]">
						<div className="font-semibold mb-1">{i18n.t("editor:validate.documentError")}</div>
						<div className="text-[var(--lilia-text-muted)] whitespace-pre-wrap break-words">
							{documentValidation.error}
						</div>
					</div>
				)}

				{(documentValidation?.warnings?.length ?? 0) > 0 && (
					<ul
						data-testid="validate-rail-warnings"
						className="space-y-1.5"
					>
						{documentValidation?.warnings?.map((w, i) => (
							<li
								key={`${w}-${i}`}
								data-testid="validate-warning"
								className="flex items-start gap-2 text-[12px] text-[var(--lilia-text)]"
							>
								<IconAlertTriangle
									size={12}
									className="shrink-0 mt-0.5 text-[var(--lilia-warning,#d97706)]"
								/>
								<span className="leading-relaxed">{w}</span>
							</li>
						))}
					</ul>
				)}

				{status === "ok" && firstPass && (
					<p data-testid="firstwin-validate" className="text-[13px] leading-[1.5] text-[var(--lilia-text)]">
						{i18n.t("editor:firstWin.validate")}
					</p>
				)}

				{status === "ok" && (
					<p data-testid="validation-ok" className="text-[12px] text-[var(--lilia-text-muted)] leading-relaxed">
						{i18n.t("editor:validate.thisDocumentCompiledWithNo")}
					</p>
				)}

				{status === "stale" && (
					<p data-testid="validation-stale" className="text-[12px] text-[var(--lilia-text-muted)] leading-relaxed">
						{i18n.t("editor:validate.itCompiledCleanlyLastTime")}
					</p>
				)}

				{status === "idle" && (
					<p data-testid="validation-idle" className="text-[12px] text-[var(--lilia-text-muted)] leading-relaxed">
						{i18n.t("editor:validate.thisDocumentHasNotBeen")}
					</p>
				)}

				{status === "idle" && (
					<p className="text-[12px] text-[var(--lilia-text-muted)] leading-relaxed">
						{i18n.t("editor:validate.saveTheDocumentOrRun")}
					</p>
				)}

				{onOpenStudio && (
					<button
						type="button"
						data-testid="validate-open-full"
						onClick={onOpenStudio}
						className="inline-flex items-center gap-1.5 text-[11.5px] text-[var(--lilia-primary)] hover:underline"
					>
						{i18n.t("editor:validate.openFullValidation")}
						<IconExternalLink size={11} />
					</button>
				)}

			</div>
		</div>
	);
}
